import React from 'react';
import { Link } from '../router';
import { useAuth, formatPrice, PLAN_NAMES } from '../auth';
import { SITE } from '../config';
import { PricingCards, SiteLayout } from '../components/Site';

// These pages are templates. Review them (ideally with a professional) and fill in
// your real business details in src/config.ts before accepting payments.
const UPDATED = 'October 24, 2026';

function Doc({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <SiteLayout>
      <article className="doc">
        <h1>{title}</h1>
        <p className="updated">Last updated {UPDATED}</p>
        {children}
      </article>
    </SiteLayout>
  );
}

const mail = <a href={`mailto:${SITE.supportEmail}`}>{SITE.supportEmail}</a>;

export function Terms() {
  const { config } = useAuth();
  return (
    <Doc title="Terms of Service">
      <p>
        These terms govern your use of {SITE.name} (the "Service"), operated by {SITE.businessName}. By creating an
        account you agree to them.
      </p>
      <h2>Your account</h2>
      <p>You are responsible for keeping your login details secure and for activity under your account. You must be at least 13 years old to use the Service.</p>
      <h2>Plans</h2>
      <p>
        The Free plan includes unlimited saved problems, the review schedule, the browser extension and{' '}
        {config!.freeMonthlyGrades} AI-graded answers per calendar month.{' '}
        {config!.plans.map(p => (
          <React.Fragment key={p.key}>
            {PLAN_NAMES[p.tier]} ({formatPrice(p.amount)} per {p.period === 'monthly' ? 'month' : 'year'}){' '}
            {p.monthlyGrades ? `raises this to ${p.monthlyGrades} per month` : 'removes the AI grading limit'}.{' '}
          </React.Fragment>
        ))}
        Plan features may change; we'll give notice of material changes.
      </p>
      <h2>Subscriptions and billing</h2>
      <p>
        Paid plans are billed in advance through Razorpay. Subscriptions renew automatically until cancelled. You can
        cancel at any time from Settings; your plan stays active until the end of the paid period. See the{' '}
        <Link to="/refunds">Refund Policy</Link> for refunds.
      </p>
      <h2>Acceptable use</h2>
      <p>Don't misuse the Service: no attempts to disrupt it, access other users' data, or resell AI grading. We may suspend accounts that do.</p>
      <h2>Your content</h2>
      <p>You own the notes and approaches you save. You grant us the limited rights needed to store them and send them to our AI provider when you request grading.</p>
      <h2>Disclaimer</h2>
      <p>The Service is provided "as is". AI grading can be wrong; treat it as a study aid. To the extent permitted by law, our liability is limited to the amount you paid us in the past 12 months.</p>
      <h2>Contact</h2>
      <p>Questions about these terms: {mail}.</p>
    </Doc>
  );
}

export function Privacy() {
  const { config } = useAuth();
  return (
    <Doc title="Privacy Policy">
      <h2>What we collect</h2>
      <ul>
        <li><strong>Account details:</strong> your name and email{config!.googleEnabled && ', plus your profile picture if you sign in with Google'}.</li>
        <li><strong>Study data:</strong> the problems you save, your notes, approaches and review history.</li>
        <li><strong>Payment details:</strong> handled by Razorpay. We store only the subscription and payment IDs, never card numbers.</li>
        <li><strong>Technical data:</strong> standard server logs (IP address, timestamps) used for security and debugging.</li>
      </ul>
      <h2>How we use it</h2>
      <p>To run the Service: show your schedule, sync the extension, process payments, and prevent abuse. We don't sell your data or show ads.</p>
      <h2>AI grading</h2>
      <p>When you submit an answer for grading, the problem title, your saved approach and notes, and your answer are sent to OpenAI to produce a verdict. Under OpenAI's API terms this data isn't used to train their models.</p>
      <h2>Cookies</h2>
      <p>We use a single essential cookie to keep you signed in. No tracking or advertising cookies.</p>
      <h2>Retention and deletion</h2>
      <p>You can export your data at any time from Settings. To delete your account and all data, email {mail}; we'll do it within 30 days.</p>
      <h2>Contact</h2>
      <p>{SITE.businessName}, {SITE.businessAddress}. Email: {mail}.</p>
    </Doc>
  );
}

export function Refunds() {
  const { config } = useAuth();
  return (
    <Doc title="Refund & Cancellation Policy">
      <h2>Cancellation</h2>
      <p>
        You can cancel a paid plan at any time from <Link to="/app/settings">Settings</Link>. Cancellation stops future
        renewals, and the plan remains active until the end of your current billing period.
      </p>
      <h2>Refunds</h2>
      <ul>
        <li>
          New subscriptions ({config!.plans.map(p => `${PLAN_NAMES[p.tier]} ${formatPrice(p.amount)}/${p.period === 'monthly' ? 'month' : 'year'}`).join(', ')})
          can be refunded in full if you ask within 7 days of the first payment.
        </li>
        <li>Renewal charges can be refunded if you ask within 48 hours of the renewal and haven't used paid features since.</li>
        <li>Approved refunds go back to the original payment method within 5–7 business days.</li>
      </ul>
      <h2>Delivery</h2>
      <p>{SITE.name} is a digital service. Paid features are activated on your account immediately after payment; nothing is shipped.</p>
      <h2>How to request</h2>
      <p>Email {mail} from your account email with the payment ID from your receipt.</p>
    </Doc>
  );
}

export function Contact() {
  return (
    <Doc title="Contact us">
      <p>We usually reply within one business day.</p>
      <h2>Support and billing</h2>
      <p>Email: {mail}</p>
      <h2>Business details</h2>
      <p>{SITE.businessName}<br />{SITE.businessAddress}</p>
    </Doc>
  );
}

export function PricingPage() {
  return (
    <SiteLayout>
      <section className="section">
        <div className="container">
          <div className="section-head">
            <div className="eyebrow">Pricing</div>
            <h1 className="section-title">Simple pricing. Free forever plan.</h1>
            <p className="section-sub">Everything that helps you remember is free. Plus and Pro add more AI grading.</p>
          </div>
          <PricingCards />
          <p className="section-sub" style={{ textAlign: 'center', fontSize: 14 }}>
            Prices in INR, including applicable taxes. Cancel anytime. <Link to="/refunds">Refund policy</Link>.
          </p>
        </div>
      </section>
    </SiteLayout>
  );
}

export function NotFound() {
  return (
    <SiteLayout>
      <div className="doc" style={{ textAlign: 'center' }}>
        <h1>Page not found</h1>
        <p className="updated">The page you're looking for doesn't exist.</p>
        <Link to="/" className="btn btn-primary">Go home</Link>
      </div>
    </SiteLayout>
  );
}
